/**
 * PR-CORE-01 — canonical release-evidence ingestion path.
 *
 * The one place runtime truth consumes verified external proof (CI run,
 * staging/production deploy, real-device/owner sign-off). Binding is strict
 * and fail-closed:
 *
 *  - Every SHA-like field present on a record (headSha / deploySha /
 *    sourceSha) must equal the source under test — a single contradictory
 *    field rejects the whole record; there is no precedence fallback.
 *  - `production_deploy`/`staging_deploy` require a bound `deploySha`;
 *    `ci_run` requires a bound `headSha`/`sourceSha`; `real_device_proof`/
 *    `owner_signoff` must carry an explicit bound SHA.
 *  - Kind and environment are coherently bound: `production_deploy` only
 *    counts in `production`, `staging_deploy` only in `staging`, `ci_run`
 *    only in `ci`, `real_device_proof` only in `real_device`. A record whose
 *    kind/environment disagree is rejected — it can never promote LIVE.
 *    `owner_signoff` stays environment-free: an owner sign-off can
 *    legitimately cover any verification scope; `code_inspection`,
 *    `unit_test` and `integration_test` also occur across environments and
 *    are intentionally not pinned.
 *  - `ci_run` records must carry a normalized `ciStatus` of exactly
 *    `success` or `failure` — anything else is rejected rather than folded
 *    into `ci_failed`.
 *  - `capabilityIds` must be entirely known and unique — a mixed
 *    valid+unknown list rejects the record.
 *  - `checkedAt` must be a real ISO timestamp and SHAs must be full git
 *    SHAs — malformed values reject the record.
 *  - Derived `buildVerification` is deterministic: the newest valid `ci_run`
 *    record wins regardless of input array order.
 *
 * Read-only: records only strengthen truth entries; they never grant any
 * capability beyond what the canonical registry allows.
 */
import { listCanonicalProductEntries } from "../canonical-product/index.js";
import { PRODUCT_RUNTIME_ENVIRONMENTS, RELEASE_EVIDENCE_KINDS, RUNTIME_EQUIVALENCE_REASONS, } from "./productRuntimeTruthTypes.js";
const ENV_SET = new Set(PRODUCT_RUNTIME_ENVIRONMENTS);
const KIND_SET = new Set(RELEASE_EVIDENCE_KINDS);
const ISO_TS_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
const GIT_SHA_RE = /^[0-9a-f]{40}$/i;
/** Kinds that count as deployment proof for `deployStatus`. */
const DEPLOY_EVIDENCE_KINDS = [
    "production_deploy",
    "staging_deploy",
];
/**
 * Kinds pinned to exactly one environment. Evidence produced by one kind of
 * verification cannot be relabelled to claim a stronger scope — a
 * `production_deploy` record must actually have been produced in
 * `production`. `owner_signoff` and the test/inspection kinds are
 * intentionally absent: they legitimately span environments.
 */
const KIND_REQUIRED_ENV = {
    production_deploy: "production",
    staging_deploy: "staging",
    ci_run: "ci",
    real_device_proof: "real_device",
};
/** Normalized runtime CI statuses a `ci_run` record may carry. */
const VALID_CI_STATUSES = new Set(["success", "failure"]);
/** True when a record's kind/environment pair is coherent (pinned kinds only). */
export function evidenceKindEnvironmentCoherent(e) {
    const required = KIND_REQUIRED_ENV[e.kind];
    return required === undefined || e.environment === required;
}
/** True when `ciStatus` is a normalized runtime value (`success`/`failure`). */
export function isNormalizedCiStatus(status) {
    return status !== undefined && VALID_CI_STATUSES.has(status);
}
/**
 * True when `value` is a real parseable ISO-8601 timestamp, not merely a
 * prefix-shaped string.
 */
function isRealIsoTimestamp(value) {
    return ISO_TS_RE.test(value) && !Number.isNaN(Date.parse(value));
}
/** True when `value` is a well-formed full git SHA (40 hex). */
export function isGitSha(value) {
    return typeof value === "string" && GIT_SHA_RE.test(value);
}
/**
 * Strict binding: returns the source SHA when EVERY present SHA-like field
 * (headSha, deploySha, sourceSha) equals it. Any contradictory field, any
 * malformed SHA, or no SHA at all means the record does not bind — there is
 * no precedence fallback that could hide a stale deploySha behind a fresh
 * headSha. `equivalentShas` relaxes exactly one step: a record whose bound
 * SHA is provably equivalent to the source (docs-only delta, verified by the
 * generator/gate — never by the record itself) also binds.
 */
export function evidenceBindsTo(e, sourceSha, equivalentShas) {
    if (sourceSha === "unversioned")
        return false;
    const fields = [e.headSha, e.deploySha, e.sourceSha].filter((v) => v !== undefined);
    if (fields.length === 0)
        return false;
    if (fields.some((f) => !isGitSha(f)))
        return false;
    const bound = fields[0];
    if (!fields.every((f) => f === bound))
        return false;
    return bound === sourceSha || (equivalentShas?.has(bound) ?? false);
}
/** The SHA a record claims to prove — only meaningful when internally consistent. */
export function evidenceBoundSha(e) {
    const fields = [e.headSha, e.deploySha, e.sourceSha].filter((v) => v !== undefined);
    if (fields.length === 0)
        return undefined;
    return fields.every((f) => f === fields[0]) ? fields[0] : undefined;
}
/** True when the record is bound to the source under test (direct or equivalent). */
export function isEvidenceBoundToSource(e, sourceSha, equivalentShas) {
    return evidenceBindsTo(e, sourceSha, equivalentShas);
}
/** True when the record is a SHA-bound production deploy of `sourceSha`. */
export function isProductionDeployEvidence(e, sourceSha, equivalentShas) {
    return (e.kind === "production_deploy" &&
        e.environment === "production" &&
        evidenceBindsTo(e, sourceSha, equivalentShas));
}
const REASON_SET = new Set(RUNTIME_EQUIVALENCE_REASONS);
/**
 * Shape-validation for runtime-equivalence claims. Only the claim's shape is
 * checked here — the semantic proof (that the delta between evidenceSha and
 * the source is genuinely non-runtime) is computed by the repo generator/gate
 * from git and passed in. A malformed or self-referential claim never lands.
 */
export function validateRuntimeEquivalenceClaim(claim, sourceSha) {
    if (!isGitSha(claim.evidenceSha))
        return "malformed evidenceSha";
    if (claim.evidenceSha === sourceSha)
        return "self-equivalence is meaningless";
    if (!REASON_SET.has(claim.reason))
        return `unknown equivalence reason '${claim.reason}'`;
    if (claim.changedPaths !== undefined &&
        (!Array.isArray(claim.changedPaths) ||
            claim.changedPaths.length === 0 ||
            claim.changedPaths.some((p) => typeof p !== "string" || p.length === 0))) {
        return "changedPaths must be a non-empty list of paths";
    }
    return undefined;
}
/**
 * Validate one record against `sourceSha` and the canonical capability id set.
 * Returns a rejection reason, or undefined when the record is acceptable.
 */
/**
 * Shape-only validation for deploy records: environment, kind pin, ISO
 * checkedAt and internally-consistent SHA fields — but NO source binding.
 * A deploy that happened is a fact even when the source it proves differs
 * from the one under test.
 */
export function validateDeployEvidenceShape(record) {
    if (!ENV_SET.has(record.environment))
        return `unknown environment '${record.environment}'`;
    if (!DEPLOY_EVIDENCE_KINDS.includes(record.kind))
        return `not a deploy record`;
    const requiredEnv = KIND_REQUIRED_ENV[record.kind];
    if (requiredEnv !== undefined && record.environment !== requiredEnv) {
        return `kind '${record.kind}' cannot claim environment '${record.environment}'`;
    }
    if (!isRealIsoTimestamp(record.checkedAt))
        return "missing/invalid ISO checkedAt";
    const fields = [record.headSha, record.deploySha, record.sourceSha].filter((v) => v !== undefined);
    if (fields.length === 0)
        return "deploy record carries no SHA field";
    if (fields.some((f) => !isGitSha(f)))
        return "malformed SHA field";
    if (!fields.every((f) => f === fields[0]))
        return "contradictory SHA fields";
    return undefined;
}
export function validateReleaseEvidenceRecord(record, sourceSha, canonicalIds, equivalentShas) {
    if (!ENV_SET.has(record.environment))
        return `unknown environment '${record.environment}'`;
    if (!KIND_SET.has(record.kind))
        return `unknown evidence kind '${record.kind}'`;
    const requiredEnv = KIND_REQUIRED_ENV[record.kind];
    if (requiredEnv !== undefined && record.environment !== requiredEnv) {
        return `kind '${record.kind}' cannot claim environment '${record.environment}'`;
    }
    if (!isRealIsoTimestamp(record.checkedAt))
        return "missing/invalid ISO checkedAt";
    for (const f of [record.headSha, record.deploySha, record.sourceSha]) {
        if (f !== undefined && !isGitSha(f))
            return "malformed SHA field";
    }
    if (!evidenceBindsTo(record, sourceSha, equivalentShas)) {
        return sourceSha === "unversioned"
            ? "cannot bind evidence to unversioned source"
            : "evidence sha does not match source or a verified-equivalent snapshot (all present SHA fields must agree)";
    }
    // Kind-specific binding requirements.
    if (record.kind === "production_deploy" || record.kind === "staging_deploy") {
        if (record.deploySha === undefined)
            return `${record.kind} requires deploySha bound to source`;
    }
    if (record.kind === "ci_run") {
        if (record.headSha === undefined && record.sourceSha === undefined) {
            return "ci_run requires headSha or sourceSha bound to source";
        }
        if (!isNormalizedCiStatus(record.ciStatus)) {
            return `ci_run requires ciStatus of 'success' or 'failure'`;
        }
    }
    // capabilityIds: unknown ids or duplicates reject the record outright.
    if (record.capabilityIds !== undefined) {
        const seen = new Set();
        for (const id of record.capabilityIds) {
            if (!canonicalIds.includes(id))
                return `unknown capabilityId '${id}'`;
            if (seen.has(id))
                return `duplicate capabilityId '${id}'`;
            seen.add(id);
        }
        if (record.capabilityIds.length === 0)
            return "empty capabilityIds";
    }
    return undefined;
}
/**
 * Ingest external release evidence for `sourceSha`. Records that are malformed,
 * unbound, internally contradictory, or targeted at unknown capabilities are
 * rejected — they never reach the truth snapshot.
 */
export function ingestReleaseEvidence(records, sourceSha, runtimeEquivalences) {
    const canonicalIds = listCanonicalProductEntries().filter((e) => !e.deprecated).map((e) => e.id);
    const overrides = new Map();
    const accepted = [];
    const rejected = [];
    let deployStatus = "not_deployed";
    let deployedSha;
    const ciRuns = [];
    // Valid claims only: shape-checked, deduped by evidenceSha, non-self.
    const equivalentShas = new Set();
    const validClaims = new Map();
    for (const claim of runtimeEquivalences ?? []) {
        if (validateRuntimeEquivalenceClaim(claim, sourceSha) !== undefined)
            continue;
        if (!validClaims.has(claim.evidenceSha))
            validClaims.set(claim.evidenceSha, claim);
        equivalentShas.add(claim.evidenceSha);
    }
    const appliedEquivalences = new Map();
    const push = (capId, ev) => {
        const list = overrides.get(capId) ?? [];
        list.push(ev);
        overrides.set(capId, list);
    };
    (records ?? []).forEach((record, index) => {
        const reason = validateReleaseEvidenceRecord(record, sourceSha, canonicalIds, equivalentShas);
        if (reason !== undefined) {
            rejected.push({ index, reason });
            return;
        }
        accepted.push(record);
        const bound = evidenceBoundSha(record);
        if (bound !== undefined && bound !== sourceSha) {
            const claim = validClaims.get(bound);
            if (claim)
                appliedEquivalences.set(bound, claim);
        }
        const targets = record.capabilityIds ?? canonicalIds;
        const { capabilityIds: _ignored, ...ev } = record;
        for (const id of targets)
            push(id, ev);
        if (record.kind === "ci_run")
            ciRuns.push(record);
        if (DEPLOY_EVIDENCE_KINDS.includes(record.kind)) {
            // Deploy status is order-independent: production outranks staging.
            if (record.kind === "production_deploy" || deployStatus !== "production") {
                deployStatus = record.kind === "production_deploy" ? "production" : "staging";
            }
        }
    });
    // Shape-valid deploys are facts — bound or not. Facts feed deployedSha;
    // only bound ones may promote deployStatus (collected above).
    const deployEvidence = (records ?? []).filter((r) => DEPLOY_EVIDENCE_KINDS.includes(r.kind) && validateDeployEvidenceShape(r) === undefined);
    if (deployEvidence.length > 0) {
        const newest = deployEvidence.reduce((a, b) => Date.parse(a.checkedAt) >= Date.parse(b.checkedAt) ? a : b);
        deployedSha = newest.deploySha ?? evidenceBoundSha(newest);
    }
    // Deterministic buildVerification: the newest valid ci_run by checkedAt wins.
    let buildVerification = "unknown";
    if (ciRuns.length > 0) {
        const newest = ciRuns.reduce((a, b) => Date.parse(a.checkedAt) >= Date.parse(b.checkedAt) ? a : b);
        buildVerification = newest.ciStatus === "success" ? "ci_passed" : "ci_failed";
    }
    const evidenceOverrides = {};
    for (const [id, list] of overrides)
        evidenceOverrides[id] = list;
    return Object.freeze({
        evidenceOverrides: Object.freeze(evidenceOverrides),
        deployStatus,
        buildVerification,
        accepted: Object.freeze(accepted),
        rejected: Object.freeze(rejected),
        deployEvidence: Object.freeze(deployEvidence),
        appliedEquivalences: Object.freeze([...appliedEquivalences.values()]),
        ...(deployedSha !== undefined ? { deployedSha } : {}),
    });
}
//# sourceMappingURL=releaseEvidence.js.map